import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { chiama } from '../api/client.js';
import type { Utente } from '../accesso/useSessione.js';
import { Contesto, type SquadraStagione } from './contesto.js';

/**
 * Carica una volta per sessione le squadre-stagione dell'utente. Non blocca
 * il rendering: la panchina si apre con la sua sola richiesta anche se
 * questa e lenta o fallisce, e a bordo campo e l'unica cosa che conta.
 */
export function ProviderContesto({
  utente,
  esci,
  children,
}: {
  utente: Utente;
  esci: () => Promise<void>;
  children: ReactNode;
}) {
  // Il Super Admin senza societa non ha squadre: nessuna richiesta da fare.
  const senzaSocieta = !utente.societaId;
  const [squadre, setSquadre] = useState<SquadraStagione[] | null>(senzaSocieta ? [] : null);
  const [erroreSquadre, setErroreSquadre] = useState<string | null>(null);

  useEffect(() => {
    if (senzaSocieta) return;
    let vivo = true;
    chiama<SquadraStagione[]>('/squadre-stagione')
      .then((s) => vivo && setSquadre(s))
      .catch(
        (e: unknown) =>
          vivo && setErroreSquadre(e instanceof Error ? e.message : 'Squadre non disponibili.'),
      );
    return () => {
      vivo = false;
    };
  }, [senzaSocieta]);

  const ricaricaSquadre = useCallback(async () => {
    if (senzaSocieta) return;
    try {
      setSquadre(await chiama<SquadraStagione[]>('/squadre-stagione'));
      setErroreSquadre(null);
    } catch (e) {
      setErroreSquadre(e instanceof Error ? e.message : 'Squadre non disponibili.');
    }
  }, [senzaSocieta]);

  const valore = useMemo(
    () => ({ utente, squadre, erroreSquadre, ricaricaSquadre, esci }),
    [utente, squadre, erroreSquadre, ricaricaSquadre, esci],
  );

  return <Contesto.Provider value={valore}>{children}</Contesto.Provider>;
}
